/* Version: 3.5.149 */
'use client';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useRef, useReducer, useState, useEffect } from 'react';
import i18n from '../../lang/i18n/index.js';
import { noop } from '../../utils/functions.js';
import { getSide, POPOVER_COMPONENT_NAMES } from '../../utils/popoverPosition.js';
import clickReducer from './clickReducer.js';
import { DEFAULT_STATE, TOOLTIP_SIDES, TOOLTIP_ACTIONS } from './constants.js';
import hoverReducer from './hoverReducer.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useLang from '../../hooks/useLang.js';
import useOnClickOutside from '../../hooks/useOnClickOutside.js';
import useKeyPress from '../../hooks/useKeyPress.js';

var HOVER_START = TOOLTIP_ACTIONS.HOVER_START,
  HOVER_END = TOOLTIP_ACTIONS.HOVER_END,
  FOCUS_START = TOOLTIP_ACTIONS.FOCUS_START,
  FOCUS_END = TOOLTIP_ACTIONS.FOCUS_END,
  CLICK_TOGGLE = TOOLTIP_ACTIONS.CLICK_TOGGLE,
  CLICK_CLOSE = TOOLTIP_ACTIONS.CLICK_CLOSE,
  CLICK_OUTSIDE = TOOLTIP_ACTIONS.CLICK_OUTSIDE,
  PRESS_ESCAPE = TOOLTIP_ACTIONS.PRESS_ESCAPE,
  DEFERRED_CLOSE = TOOLTIP_ACTIONS.DEFERRED_CLOSE;
function useTooltip(_ref) {
  var controlledId = _ref.id,
    controlledSide = _ref.side,
    ariaLabel = _ref.ariaLabel,
    disableAriaForPopup = _ref.disableAriaForPopup,
    title = _ref.title,
    content = _ref.content,
    bubbleWidth = _ref.bubbleWidth,
    activateOnHover = _ref.hover,
    variant = _ref.variant;
  var _useDerivedState = useDerivedState(useId('tooltip-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var wrapperRef = useRef();
  var toggleRef = useRef();
  var lang = useLang();
  var reducer = activateOnHover ? hoverReducer : clickReducer;
  var _useReducer = useReducer(reducer, DEFAULT_STATE),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    _useReducer2$ = _useReducer2[0],
    showTooltip = _useReducer2$.showTooltip,
    deferredClose = _useReducer2$.deferredClose,
    dispatch = _useReducer2[1];
  var _useState = useState(undefined),
    _useState2 = _slicedToArray(_useState, 2),
    side = _useState2[0],
    setSide = _useState2[1];

  // we want to make sure that our toggleRef is defined, so we need to access it imperatively rather than inline (unfortunately)
  useEffect(function () {
    if (showTooltip) {
      setSide(getSide(toggleRef,
      // If side isn't one of our supported sides, we'll ignore it; this lets getSide determine the side automatically
      Object.values(TOOLTIP_SIDES).includes(controlledSide) ? controlledSide : undefined, bubbleWidth, POPOVER_COMPONENT_NAMES.TOOLTIP));
    } else {
      setSide(undefined);
    }
  }, [showTooltip, bubbleWidth, controlledSide]);

  // Our tooltip needs to not close immediately as soon as the cursor exits an element
  // So, we add a tiny delay to defer the close, so that the component can "double check" that the element isn't actually focused
  useEffect(function () {
    if (deferredClose) {
      var timeoutId = setTimeout(function () {
        return dispatch({
          type: DEFERRED_CLOSE
        });
      }, 1);
      return function () {
        return clearTimeout(timeoutId);
      };
    }
    return noop;
  }, [deferredClose]);
  useOnClickOutside(wrapperRef, function () {
    return dispatch({
      type: CLICK_OUTSIDE
    });
  });
  var escPressed = useKeyPress('Escape');
  if (showTooltip && escPressed) {
    dispatch({
      type: PRESS_ESCAPE
    });
  }
  var toggleId = "".concat(id, "-toggle");
  var tooltipId = "".concat(id, "-wrapper");
  var iconId = "".concat(id, "-icon");
  return {
    tooltipContent: content || title,
    showTooltip: side && showTooltip,
    // don't show tooltip unless we've also calculated a side to render on
    showCloseButton: !activateOnHover,
    wrapperProps: {
      ref: wrapperRef
    },
    toggleProps: {
      showTooltip: showTooltip,
      activateOnHover: activateOnHover,
      ref: toggleRef,
      id: toggleId,
      'aria-describedby': showTooltip && !disableAriaForPopup ? tooltipId : undefined,
      'aria-label': ariaLabel || i18n[lang].tooltip.ariaLabelDefault,
      onMouseOver: function onMouseOver() {
        return dispatch({
          type: HOVER_START
        });
      },
      onMouseOut: function onMouseOut() {
        return dispatch({
          type: HOVER_END
        });
      },
      onFocus: function onFocus() {
        return dispatch({
          type: FOCUS_START
        });
      },
      onBlur: function onBlur() {
        return dispatch({
          type: FOCUS_END
        });
      },
      onClick: function onClick() {
        return dispatch({
          type: CLICK_TOGGLE
        });
      },
      onTouchEnd: function onTouchEnd(event) {
        event.preventDefault();
        dispatch({
          type: CLICK_TOGGLE
        });
        toggleRef.current.focus();
      }
    },
    tooltipProps: {
      side: side,
      bubbleWidth: bubbleWidth,
      id: tooltipId,
      'aria-hidden': !!disableAriaForPopup,
      onMouseOver: function onMouseOver() {
        return dispatch({
          type: HOVER_START
        });
      },
      onMouseOut: function onMouseOut() {
        return dispatch({
          type: HOVER_END
        });
      }
    },
    arrowProps: {
      side: side
    },
    closeButtonProps: {
      'aria-label': i18n[lang].tooltip.close,
      onClick: function onClick() {
        dispatch({
          type: CLICK_CLOSE
        });
        toggleRef.current.focus();
      }
    },
    defaultIconProps: {
      variant: variant,
      id: iconId
    }
  };
}

export { useTooltip as default };
